import { useState, useEffect, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Search, Upload, Loader2, Mail } from "lucide-react";

// -------------------- Shared ISBN Scanner (html5-qrcode) --------------------
export function IsbnScannerModal({ onClose, onScanned }) {
  const containerId = "shared-isbn-scanner";
  const scannerRef = useRef(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (cancelled) return;
        const scanner = new Html5Qrcode(containerId);
        scannerRef.current = scanner;
        const config = { fps: 10, qrbox: { width: 260, height: 140 } };
        await scanner.start(
          { facingMode: "environment" },
          config,
          (decoded) => {
            const clean = String(decoded || "").replace(/[^0-9Xx]/g, "");
            if (clean.length >= 10) onScanned(clean);
          },
          () => { /* per-frame decode error — ignore */ }
        );
      } catch (e) {
        setErr(String(e?.message || e) + " — allow camera access, or use a device with a camera.");
      }
    })();
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) { s.stop().catch(() => {}).finally(() => { try { s.clear(); } catch { /* noop */ } }); }
    };
  }, [onScanned]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="isbn-scanner-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md border-2 border-[#09090b]">
        <div className="p-3 border-b-2 border-[#09090b] flex items-center justify-between bg-[#09090b] text-white">
          <div className="font-bold text-sm">Scan ISBN Barcode</div>
          <button onClick={onClose} data-testid="isbn-scanner-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4">
          <div id={containerId} style={{ width: "100%", minHeight: 260 }} />
          {err && <div className="mt-3 text-xs text-[#dc2626] font-bold">{err}</div>}
          <div className="text-[11px] text-[#71717a] mt-2">Point the camera at the ISBN barcode. Item will auto-add.</div>
        </div>
      </div>
    </div>
  );
}

// -------------------- Shared Bill Photo → OCR Extract Button --------------------
// Uploads a photo of another company's invoice and calls the vision LLM
// endpoint to auto-fill line items. Parent decides how to map the extracted
// items into its own item shape (Manual/Proforma vs Library/OB differ).
export function ExtractFromImageButton({ kind, onExtracted, testid = "extract-from-image" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image must be under 8 MB"); return; }
    setBusy(true);
    const t = toast.loading("Reading the bill photo… this can take a few seconds");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/extract-invoice-items?kind=${kind}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const items = data?.items || [];
      if (!items.length) {
        toast.error("Could not detect any line items — please add them manually", { id: t });
        return;
      }
      onExtracted(items);
      toast.success(`Extracted ${items.length} item${items.length === 1 ? "" : "s"} — edit and save when ready`, { id: t });
    } catch (e) { toast.error(apiError(e), { id: t }); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" data-testid={`${testid}-input`} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="border-2 border-[#ea580c] bg-[#fff7ed] hover:bg-[#ea580c] hover:text-white text-[#ea580c] px-3 py-1.5 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-60" data-testid={testid}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        {busy ? "Reading…" : "Upload Bill Photo"}
      </button>
    </>
  );
}


// -------------------- Shared Email Invoice Dialog --------------------
// Small modal used to send any invoice PDF (Manual, Proforma, Library or
// Other Company Bill) to a customer/library. Pre-fills the recipient email
// from `defaultTo` and supports an optional short note.
export function EmailInvoiceDialog({ open, onClose, endpoint, defaultTo = "", defaultCc = "", docNumber = "" }) {
  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState(defaultCc);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTo(defaultTo); setCc(defaultCc); setNote(""); }, [defaultTo, defaultCc, open]);
  if (!open) return null;

  const send = async (e) => {
    e?.preventDefault?.();
    if (!to || !to.includes("@")) { toast.error("Please enter a valid recipient email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { to_email: to, cc_email: cc || null, note });
      toast.success(`Emailed to ${data.to}`);
      onClose();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="email-invoice-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={send} className="bg-white w-full max-w-md border-2 border-[#09090b]">
        <div className="p-3 border-b-2 border-[#09090b] flex items-center justify-between bg-[#09090b] text-white">
          <div className="font-bold text-sm">Email Invoice{docNumber ? ` · ${docNumber}` : ""}</div>
          <button type="button" onClick={onClose} data-testid="email-invoice-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#71717a] mb-1">To *</div>
            <input type="email" required value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@library.edu" className="w-full border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-2 text-sm outline-none" data-testid="email-invoice-to" />
          </label>
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#71717a] mb-1">CC (optional)</div>
            <input type="email" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="accounts@..." className="w-full border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-2 text-sm outline-none" data-testid="email-invoice-cc" />
          </label>
          <label className="block">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#71717a] mb-1">Note (optional)</div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Add a short message the library will see above the invoice…" className="w-full border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-2 text-sm outline-none" data-testid="email-invoice-note" />
          </label>
        </div>
        <div className="p-3 border-t-2 border-[#09090b] flex items-center justify-end gap-2 bg-white">
          <button type="button" onClick={onClose} disabled={busy} className="px-3 py-1.5 text-xs font-bold border-2 border-[#e4e4e7] hover:border-[#09090b] disabled:opacity-50" data-testid="email-invoice-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="px-4 py-1.5 text-xs font-bold bg-[#ea580c] hover:bg-[#c2410c] text-white disabled:opacity-60 flex items-center gap-1.5" data-testid="email-invoice-send">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            {busy ? "Sending…" : "Send Email"}
          </button>
        </div>
      </form>
    </div>
  );
}



// -------------------- Shared Catalog Title Autocomplete --------------------
export function TitleAutocomplete({ onPick, placeholder = "Search catalog by title / ISBN / author…", testid = "title-autocomplete" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/books`, { params: { q, limit: 8 } });
        setResults(data.items || []);
      } catch (e) { /* ignore */ } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timerRef.current);
  }, [q]);

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        <Search className="w-3.5 h-3.5 text-[#71717a]" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 200)} placeholder={placeholder} className="border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-1.5 text-xs outline-none w-72" data-testid={testid} />
      </div>
      {open && q && (
        <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border-2 border-[#09090b] max-h-72 overflow-y-auto shadow-xl">
          {loading && <div className="p-3 text-xs text-[#71717a]">Searching…</div>}
          {!loading && results.length === 0 && <div className="p-3 text-xs text-[#71717a]">No matches found.</div>}
          {!loading && results.map((b) => (
            <button key={b.id} type="button"
              onMouseDown={(e) => { e.preventDefault(); onPick(b); setQ(""); setOpen(false); }}
              className="w-full text-left p-2 hover:bg-[#fff7ed] border-b border-border last:border-b-0">
              <div className="font-bold text-xs">{b.title}</div>
              <div className="text-[10px] text-[#71717a]">{b.author || "-"} · {b.publisher || "-"} · ISBN {b.isbn || "-"}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
